'use client';

import * as Sentry from 'utils/errorTracking';
import Script from 'next/script';
import { useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { safeParseLocalStorage, safeGetLocalStorage } from 'utils/localStorageHelpers';

/**
 * Subresource Integrity (SRI) hashes for statically-sourced third-party scripts (PD-35648).
 * Keyed by the exact script URL. The integrity attribute is only applied when the resolved
 * `src` matches a known key, so a different (e.g. non-prod) URL still loads without SRI.
 *
 * To rotate: download the script, run `openssl dgst -sha384 -binary <file> | openssl base64 -A`,
 * and update both the deployed asset and the hash below in the same change.
 */
const SCRIPT_SRI_HASHES: Record<string, string> = {
  'https://do1h5yobi4nsa.cloudfront.net/fingerprint-new.mini.js':
    'sha384-T8MCaS7TDDx+5D4mOz6Y6aI4/ZkvwW8bEmUft5NJnoMy52ezRrhsrtILf+jVc5VD',
};

// Helper function to log browser information for debugging (once per page load)
const logBrowserInfo = () => {
  if (typeof window === 'undefined' || (window as any).__browserInfoLogged) return;

  (window as any).__browserInfoLogged = true;
  const nav = typeof navigator !== 'undefined' ? navigator : ({} as Navigator);
  const userAgent = nav.userAgent || 'unknown';
  const platform = nav.platform || 'unknown';
  const language = nav.language || 'unknown';
  const cookieEnabled = typeof nav.cookieEnabled === 'boolean' ? nav.cookieEnabled : false;
  const onLine = typeof nav.onLine === 'boolean' ? nav.onLine : false;

  // eslint-disable-next-line no-console
  console.log('[StartEngine] Browser Info:', {
    userAgent,
    platform,
    language,
    cookieEnabled,
    onLine,
    timestamp: new Date().toISOString(),
  });
};

// Helper function to sanitize data for postMessage
const sanitizeForPostMessage = (data: any): any => {
  if (data === null || data === undefined) {
    return data;
  }

  // Handle primitive types
  if (typeof data === 'string' || typeof data === 'number' || typeof data === 'boolean') {
    return data;
  }

  // Handle arrays
  if (Array.isArray(data)) {
    return data.map((item) => sanitizeForPostMessage(item));
  }

  // Handle functions - convert to string representation
  if (typeof data === 'function') {
    return '[Function]';
  }

  // Handle DOM nodes and other unserializable objects
  if (data instanceof Node || data instanceof Element || data instanceof Window) {
    return `[Object: ${data.constructor?.name || 'Unknown'}]`;
  }

  // Handle objects with potential circular references or unserializable properties
  if (typeof data === 'object') {
    try {
      const sanitized: any = {};
      Object.keys(data).forEach((key) => {
        if (Object.prototype.hasOwnProperty.call(data, key)) {
          try {
            // Test if the property can be serialized
            JSON.stringify(data[key]);
            sanitized[key] = sanitizeForPostMessage(data[key]);
          } catch (serializationError) {
            // Replace unserializable properties with placeholder
            sanitized[key] = `[Unserializable: ${(serializationError as Error).message}]`;
          }
        }
      });
      return sanitized;
    } catch {
      return '[Object: Serialization failed]';
    }
  }

  return data;
};

const ThirdPartyScripts = () => {
  const pathname = usePathname();

  useEffect(() => {
    // Log browser information for debugging
    logBrowserInfo();

    // Initialize WebKit compatibility polyfill to prevent third-party script errors
    if (typeof window !== 'undefined' && !window.webkit) {
      window.webkit = {
        messageHandlers: {},
      };
    }

    // Initialize postMessage DataCloneError prevention
    if (typeof window !== 'undefined' && window.postMessage) {
      const originalPostMessage = window.postMessage.bind(window);

      (window as any).postMessage = function postMessageWrapper(
        message: any,
        targetOrigin?: any,
        transfer?: any,
      ) {
        try {
          // Attempt to serialize the message to check if it's cloneable
          JSON.parse(JSON.stringify(message));
          // If successful, use the original postMessage
          return originalPostMessage(message, targetOrigin, transfer);
        } catch (error) {
          // If serialization fails, sanitize the message
          // eslint-disable-next-line no-console
          console.warn(
            '[StartEngine] Sanitizing unserializable postMessage data:',
            (error as Error).message,
          );
          const sanitizedMessage = sanitizeForPostMessage(message);
          return originalPostMessage(sanitizedMessage, targetOrigin, transfer);
        }
      };
    }

    // Defensive measures for browser extension property descriptor conflicts
    // This specifically addresses issues with cryptocurrency wallet extensions (like Nightly)
    // that attempt to define window.ethereum with invalid property descriptors
    if (typeof window !== 'undefined') {
      const originalDefineProperty = Object.defineProperty;

      (Object as any).defineProperty = function definePropertyWrapper(
        obj: any,
        prop: any,
        descriptor: any,
      ) {
        if (typeof window === 'undefined') {
          return originalDefineProperty.call(this, obj, prop, descriptor);
        }
        // Check if this is an attempt to define window.ethereum by a browser extension
        if (obj === window && prop === 'ethereum' && descriptor) {
          try {
            // Validate property descriptor to prevent "Invalid property descriptor" errors
            const hasDataProps = 'value' in descriptor || 'writable' in descriptor;
            const hasAccessorProps = 'get' in descriptor || 'set' in descriptor;

            if (hasDataProps && hasAccessorProps) {
              // eslint-disable-next-line no-console
              console.warn(
                '[StartEngine] Browser extension attempted to define window.ethereum with invalid property descriptor. Using safer approach.',
              );

              // Create a safe descriptor that only uses accessor properties
              const safeDescriptor = {
                get:
                  descriptor.get ||
                  function getEthereumValue() {
                    return descriptor.value;
                  },
                set:
                  descriptor.set ||
                  function setEthereumValue(value: any) {
                    // eslint-disable-next-line no-console
                    console.warn(
                      '[StartEngine] Attempted to set window.ethereum via safe proxy:',
                      value,
                    );
                  },
                enumerable: descriptor.enumerable !== false,
                configurable: descriptor.configurable !== false,
              };

              return originalDefineProperty.call(this, obj, prop, safeDescriptor);
            }

            // Check if the property already exists and has conflicting configuration
            const existingDescriptor = Object.getOwnPropertyDescriptor(obj, prop);
            if (existingDescriptor) {
              const existingHasAccessors =
                'get' in existingDescriptor || 'set' in existingDescriptor;
              const newHasData = 'value' in descriptor || 'writable' in descriptor;

              if (existingHasAccessors && newHasData) {
                // eslint-disable-next-line no-console
                console.warn(
                  '[StartEngine] Browser extension attempted to redefine window.ethereum accessor property with data property. Preserving existing configuration.',
                );
                return obj; // Return object unchanged to prevent error
              }
            }
          } catch (validationError) {
            // eslint-disable-next-line no-console
            console.warn(
              '[StartEngine] Error validating property descriptor for window.ethereum:',
              (validationError as Error).message,
            );
            // Return object unchanged to prevent the invalid defineProperty call
            return obj;
          }
        }

        // For all other property definitions, use the original function
        try {
          return originalDefineProperty.call(this, obj, prop, descriptor);
        } catch (error) {
          // If it's a browser extension trying to redefine an existing property, handle gracefully
          if (
            error instanceof Error &&
            error.message &&
            error.message.includes('Cannot redefine property') &&
            prop === 'ethereum' &&
            obj === window
          ) {
            // eslint-disable-next-line no-console
            console.warn(
              '[StartEngine] Browser extension cannot redefine window.ethereum property:',
              error.message,
            );
            return obj; // Return object unchanged
          }
          // Re-throw other errors
          throw error;
        }
      };

      // Preserve the original function for cleanup if needed

      (Object.defineProperty as any)._startEngineOriginal = originalDefineProperty;
    }

    // Initialize comprehensive network request error handling for third-party scripts
    // IMPORTANT: This wrapper should NEVER throw errors to prevent infinite loops
    // and call stack issues with other wrappers (e.g., PostHog)
    if (typeof window !== 'undefined' && !(window as any).__fetchWrapperApplied) {
      // Mark that we've applied the wrapper to prevent double-wrapping
      (window as any).__fetchWrapperApplied = true;

      // Store the NATIVE fetch before any wrappers
      const nativeFetch = window.fetch.bind(window);

      // Override fetch to catch ALL failures gracefully
      window.fetch = async function fetchWrapper(
        input: RequestInfo | URL,
        init?: RequestInit,
      ): Promise<Response> {
        try {
          return await nativeFetch(input, init);
        } catch (error) {
          let url = 'unknown';
          if (typeof input === 'string') {
            url = input;
          } else if (input instanceof URL) {
            url = input.href;
          } else if ((input as Request)?.url) {
            url = (input as Request).url;
          }

          // Log the error but NEVER re-throw to prevent infinite loops
          // eslint-disable-next-line no-console
          console.warn(
            '[StartEngine] Fetch error suppressed:',
            (error as Error).message,
            'URL:',
            url,
          );

          // Return a safe mock response to prevent script breakage
          return new Response('{}', {
            status: 200,
            statusText: 'OK',
            headers: {
              'Content-Type': 'application/json',
            },
          });
        }
      };
    }

    const consentMode = safeGetLocalStorage('consentMode');
    if (consentMode) {
      try {
        const parsedConsentMode = safeParseLocalStorage('consentMode');
        if (parsedConsentMode) {
          // @ts-ignore-next-line
          window.gtag('consent', 'update', parsedConsentMode);
        }
      } catch (error) {
        Sentry.captureException(error);
      }
    }
  }, []);

  const _gtmScript = `
    (function(w,d,s,l,i){
      w[l]=w[l]||[];
      w[l].push({'gtm.start': new Date().getTime(), event:'gtm.js'});
      var j=d.createElement(s),
          dl=l!='dataLayer'?'&l='+l:'';
      j.async=true;
      j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;
      
      // Safe DOM insertion that works during React hydration
      // Use document.head.appendChild instead of insertBefore with stale script reference
      try {
        // Try head first (preferred for scripts)
        if (d.head) {
          d.head.appendChild(j);
        } else {
          // Fallback to body if head is not available
          d.body.appendChild(j);
        }
      } catch (e) {
        // Final fallback: try to find any safe parent to append to
        try {
          var scripts = d.getElementsByTagName('script');
          if (scripts.length > 0 && scripts[0].parentNode) {
            scripts[0].parentNode.appendChild(j);
          } else {
            // Last resort: append to document element
            d.documentElement.appendChild(j);
          }
        } catch (e2) {
          console.warn('[GTM] Failed to append script:', e2);
        }
      }
    })(window,document,'script','dataLayer', '${process.env.NEXT_PUBLIC_GTM_ID || process.env.GTM_ID}');
  `;

  const gtmIssuerScript = pathname.startsWith('/offering/')
    ? `
    // Define safe no-op functions for onPagePause and onPageResume to prevent ReferenceError
    if (typeof window.onPagePause === 'undefined') {
      window.onPagePause = function() {
        console.warn('[StartEngine] onPagePause stub called - GTM tag references undefined function');
      };
    }
    if (typeof window.onPageResume === 'undefined') {
      window.onPageResume = function() {
        console.warn('[StartEngine] onPageResume stub called - GTM tag references undefined function');
      };
    }
    
    (function(w,d,s,l,i){
      w[l]=w[l]||[];
      w[l].push({'gtm.start': new Date().getTime(), event:'gtm.js'});
      var j=d.createElement(s),
          dl=l!='dataLayer'?'&l='+l:'';
      j.async=true;
      j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;
      
      // Safe DOM insertion that works during React hydration
      // Use document.head.appendChild instead of insertBefore with stale script reference
      try {
        // Try head first (preferred for scripts)
        if (d.head) {
          d.head.appendChild(j);
        } else {
          // Fallback to body if head is not available
          d.body.appendChild(j);
        }
      } catch (e) {
        // Final fallback: try to find any safe parent to append to
        try {
          var scripts = d.getElementsByTagName('script');
          if (scripts.length > 0 && scripts[0].parentNode) {
            scripts[0].parentNode.appendChild(j);
          } else {
            // Last resort: append to document element
            d.documentElement.appendChild(j);
          }
        } catch (e2) {
          console.warn('[GTM Issuer] Failed to append script:', e2);
        }
      }
    })(window,document,'script','dataLayer', '${process.env.NEXT_PUBLIC_GTM_ID_ISSUER}');
  `
    : '';

  // Defensive script to prevent third-party script errors including jQuery dependency issues
  // This addresses various third-party scripts (verificada, GTM tags, etc.) that may execute
  // before jQuery is available, causing "$ is not defined" errors on offering pages
  const verificadaDefensiveScript = pathname.startsWith('/offering/')
    ? `
    // Add defensive measures for third-party verification scripts
    (function() {
      // Create safe global stubs for verificada objects that might be accessed before they're properly loaded
      // This prevents the "a.pdst is not a function" error by providing safe fallbacks
      var verificadaStub = {
        pdst: function(command, config) {
          console.warn('[StartEngine] Verificada pdst function called before script loaded:', command, config);
          return Promise.resolve();
        }
      };
      
      // Set up common variable names that GTM might use for verificada
      if (typeof window.a === 'undefined') {
        window.a = verificadaStub;
      }
      if (typeof window.verificada === 'undefined') {
        window.verificada = verificadaStub;
      }
      if (typeof window.v === 'undefined') {
        window.v = verificadaStub;
      }
      
      // Define getCookie function if it doesn't exist (used by verificada script)
      if (typeof window.getCookie === 'undefined') {
        window.getCookie = function(name) {
          var nameEQ = name + "=";
          var ca = document.cookie.split(';');
          for(var i = 0; i < ca.length; i++) {
            var c = ca[i];
            while (c.charAt(0) == ' ') c = c.substring(1, c.length);
            if (c.indexOf(nameEQ) == 0) return c.substring(nameEQ.length, c.length);
          }
          return null;
        };
      }
      
      // Create a safer proxy for verificada objects to prevent pdst function errors
      function createVerificadaProxy(originalObject) {
        if (!originalObject || typeof originalObject !== 'object') {
          return {
            pdst: function(command, config) {
              console.warn('[StartEngine] Verificada pdst stub called with:', command, config);
              return Promise.resolve();
            }
          };
        }
        
        // If the object exists but is missing the pdst function, add a safe stub
        if (typeof originalObject.pdst !== 'function') {
          originalObject.pdst = function(command, config) {
            console.warn('[StartEngine] Verificada pdst function was missing, using safe stub for:', command, config);
            return Promise.resolve();
          };
        }
        
        return originalObject;
      }
      
      // Monitor for verificada objects and patch them if needed
      function monitorVerificadaObjects() {
        // Check common variable names that might hold the verificada object
        var commonNames = ['verificada', 'a', 'v', 'selo', 'seal', 'badge'];
        
        commonNames.forEach(function(name) {
          if (window[name] && typeof window[name] === 'object') {
            window[name] = createVerificadaProxy(window[name]);
          }
        });
        
        // Also check for assignments in the global scope
        var originalDefineProperty = Object.defineProperty;
        Object.defineProperty = function(obj, prop, descriptor) {
          var result = originalDefineProperty.apply(this, arguments);
          
          // If this looks like a verificada object being assigned
          if (obj === window && typeof descriptor.value === 'object' && descriptor.value) {
            // Check if it's likely a verificada object (has verification-related patterns)
            var objStr = descriptor.value.toString ? descriptor.value.toString() : '';
            if (objStr.includes('verificada') || objStr.includes('pdst') || objStr.includes('conf')) {
              descriptor.value = createVerificadaProxy(descriptor.value);
            }
          }
          
          return result;
        };
      }
      
      // Start monitoring immediately
      monitorVerificadaObjects();
      
      // Also monitor periodically in case objects are created later
      var verificadaMonitorInterval = setInterval(monitorVerificadaObjects, 100);
      
      // Stop monitoring after a reasonable time
      setTimeout(function() {
        clearInterval(verificadaMonitorInterval);
      }, 10000);
      
      // Global error handler to catch and suppress third-party script errors
      var originalErrorHandler = window.onerror;
      window.onerror = function(message, source, lineno, colno, error) {
        // Check if error is from third-party scripts (including jQuery dependency errors)
        if (message && (
            message.includes("$ is not defined") ||
            message.includes("jQuery is not defined") ||
            message.includes("Cannot read properties of null (reading 'innerHTML')") ||
            message.includes("getCookie is not defined") ||
            message.includes("pdst is not a function") ||
            message.includes("a.pdst is not a function") ||
            (message.includes("pdst") && message.includes("not a function")) ||
            (source && source.includes("verificada")) ||
            (source && source.includes("selo-verificada")) ||
            (source && source.includes("mm-ga4-tagging")) ||
            (source && source.includes("ga4-tagging")) ||
            (source && source.includes("_next/static/chunks/") && message.includes("$ is not defined"))
          )) {
          console.warn('[StartEngine] Suppressed third-party script error:', message, source);
          return true; // Prevent error from bubbling up
        }
        
        // Call original error handler for other errors
        if (originalErrorHandler) {
          return originalErrorHandler.call(this, message, source, lineno, colno, error);
        }
        return false;
      };
      
      // Also handle unhandled promise rejections from third-party scripts
      var originalUnhandledRejection = window.onunhandledrejection;
      window.addEventListener('unhandledrejection', function(event) {
        if (event.reason && event.reason.message && (
            event.reason.message.includes("$ is not defined") ||
            event.reason.message.includes("jQuery is not defined") ||
            event.reason.message.includes("Cannot read properties of null (reading 'innerHTML')") ||
            event.reason.message.includes("getCookie is not defined") ||
            event.reason.message.includes("pdst is not a function") ||
            event.reason.message.includes("a.pdst is not a function") ||
            (event.reason.message.includes("pdst") && event.reason.message.includes("not a function")) ||
            event.reason.message.includes("verificada") ||
            event.reason.message.includes("ga4-tagging") ||
            (event.reason.stack && event.reason.stack.includes("_next/static/chunks/"))
          )) {
          console.warn('[StartEngine] Suppressed third-party script promise rejection:', event.reason.message);
          event.preventDefault();
          return;
        }
        
        if (originalUnhandledRejection) {
          originalUnhandledRejection.call(this, event);
        }
      });
      
      // Override getElementById to return a safe element proxy for verificada-related IDs
      var originalGetElementById = document.getElementById;
      document.getElementById = function(id) {
        var element = originalGetElementById.call(this, id);
        if (!element && id && typeof id === 'string' && 
            (id.includes('verificada') || id.includes('selo') || id.includes('seal') || 
             id.includes('badge') || id.includes('trust'))) {
          // Return a safe proxy element that prevents innerHTML errors
          var safeElement = {
            innerHTML: '',
            innerText: '',
            textContent: '',
            style: {},
            className: '',
            id: id,
            setAttribute: function() { return this; },
            getAttribute: function() { return null; },
            appendChild: function() { return this; },
            removeChild: function() { return this; },
            addEventListener: function() { return this; },
            removeEventListener: function() { return this; },
            querySelector: function() { return null; },
            querySelectorAll: function() { return []; },
            parentNode: null,
            children: [],
            childNodes: []
          };
          
          // Make innerHTML setter safe
          Object.defineProperty(safeElement, 'innerHTML', {
            get: function() { return ''; },
            set: function(value) { 
              console.warn('[StartEngine] Third-party script attempted to set innerHTML on missing element:', id);
              return '';
            },
            enumerable: true,
            configurable: true
          });
          
          return safeElement;
        }
        return element;
      };
      
      // Create a safe $ proxy that queues calls until jQuery is ready
      // This prevents "$ is not defined" errors from third-party scripts
      var jQueryCallQueue = [];
      var isJQueryReady = false;
      
      // Safe $ proxy function that queues calls or executes immediately if jQuery is ready
      function createSafejQueryProxy() {
        return function() {
          var args = Array.prototype.slice.call(arguments);
          var context = this;
          
          if (isJQueryReady && typeof window.jQuery !== 'undefined') {
            // jQuery is ready, execute immediately
            try {
              return window.jQuery.apply(context, args);
            } catch (error) {
              // Handle errors from third-party scripts
              if (error && error.message && (
                  error.message.includes("Cannot read properties of null (reading 'innerHTML')") ||
                  error.message.includes('verificada') ||
                  error.message.includes('getCookie is not defined') ||
                  error.message.includes('selo') ||
                  error.message.includes('ga4-tagging') ||
                  error.message.includes('$ is not defined') ||
                  error.message.includes('jQuery is not defined') ||
                  error.message.includes('pdst is not a function') ||
                  error.message.includes('a.pdst is not a function') ||
                  (error.message.includes('pdst') && error.message.includes('not a function'))
                )) {
                console.warn('[StartEngine] Suppressed third-party script error in jQuery proxy:', error.message);
                return null;
              }
              throw error;
            }
          } else {
            // jQuery not ready yet, queue the call
            jQueryCallQueue.push({
              args: args,
              context: context
            });
            
            // Return a chainable object for basic jQuery patterns
            return {
              ready: function(callback) {
                if (typeof callback === 'function') {
                  jQueryCallQueue.push({
                    args: [callback],
                    context: null,
                    method: 'ready'
                  });
                }
                return this;
              },
              on: function() {
                jQueryCallQueue.push({
                  args: Array.prototype.slice.call(arguments),
                  context: this,
                  method: 'on'
                });
                return this;
              },
              // Add more chainable methods as needed
              addClass: function() { return this; },
              removeClass: function() { return this; },
              css: function() { return this; },
              attr: function() { return this; },
              prop: function() { return this; },
              val: function() { return this; },
              text: function() { return this; },
              html: function() { return this; },
              hide: function() { return this; },
              show: function() { return this; },
              click: function() { return this; },
              length: 0
            };
          }
        };
      }
      
      // Set up the $ proxy immediately to catch early script execution
      if (typeof window.$ === 'undefined') {
        window.$ = createSafejQueryProxy();
      }
      
      // Enhanced jQuery ready wrapper with better error catching and call queue processing
      var jQueryInterval = setInterval(function() {
        if (typeof window.jQuery !== 'undefined') {
          clearInterval(jQueryInterval);
          isJQueryReady = true;
          
          // Replace the proxy with the real jQuery
          window.$ = window.jQuery;
          
          // Process queued calls
          while (jQueryCallQueue.length > 0) {
            var queuedCall = jQueryCallQueue.shift();
            try {
              if (queuedCall.method === 'ready' && typeof queuedCall.args[0] === 'function') {
                // Handle jQuery ready calls
                window.jQuery(queuedCall.args[0]);
              } else if (queuedCall.method === 'on') {
                // Handle event binding calls
                window.jQuery.apply(queuedCall.context, queuedCall.args);
              } else {
                // Handle general jQuery calls
                window.jQuery.apply(queuedCall.context, queuedCall.args);
              }
            } catch (error) {
              // Suppress errors from third-party scripts in queued calls
              if (error && error.message && (
                  error.message.includes("Cannot read properties of null (reading 'innerHTML')") ||
                  error.message.includes('verificada') ||
                  error.message.includes('getCookie is not defined') ||
                  error.message.includes('selo') ||
                  error.message.includes('ga4-tagging') ||
                  error.message.includes('$ is not defined') ||
                  error.message.includes('jQuery is not defined') ||
                  error.message.includes('pdst is not a function') ||
                  error.message.includes('a.pdst is not a function') ||
                  (error.message.includes('pdst') && error.message.includes('not a function'))
                )) {
                console.warn('[StartEngine] Suppressed third-party script error in queued jQuery call:', error.message);
              } else {
                console.error('[StartEngine] Error processing queued jQuery call:', error);
              }
            }
          }
          
          // Wrap jQuery.fn.ready for additional safety
          var originalReady = window.jQuery.fn.ready;
          if (originalReady && typeof originalReady === 'function') {
            window.jQuery.fn.ready = function(callback) {
              var safeCallback = function() {
                try {
                  if (typeof callback === 'function') {
                    callback.apply(this, arguments);
                  }
                } catch (error) {
                  // Suppress errors from third-party scripts
                  if (error && error.message && 
                      (error.message.includes("Cannot read properties of null (reading 'innerHTML')") ||
                       error.message.includes('verificada') ||
                       error.message.includes('getCookie is not defined') ||
                       error.message.includes('selo') ||
                       error.message.includes('ga4-tagging') ||
                       error.message.includes('$ is not defined') ||
                       error.message.includes('jQuery is not defined') ||
                       error.message.includes("pdst is not a function") ||
                       error.message.includes("a.pdst is not a function") ||
                       (error.message.includes("is not a function") && error.message.includes("pdst")))) {
                    console.warn('[StartEngine] Suppressed third-party script error in jQuery ready:', error.message);
                    return;
                  }
                  // Re-throw other errors
                  throw error;
                }
              };
              return originalReady.call(this, safeCallback);
            };
          }
        }
      }, 50); // Check more frequently for faster response
      
      // Clean up interval after reasonable time
      setTimeout(function() {
        clearInterval(jQueryInterval);
        // If jQuery still hasn't loaded, ensure $ is at least a safe no-op
        if (!isJQueryReady && typeof window.$ !== 'undefined' && typeof window.jQuery === 'undefined') {
          console.warn('[StartEngine] jQuery failed to load within timeout, maintaining safe $ proxy');
        }
      }, 10000); // Increased timeout for better reliability
      
    })();
  `
    : '';

  return (
    <>
      <Script id="google-tag-consent-script" strategy="lazyOnload">
        {`
          window.dataLayer = window.dataLayer || []; 
          function gtag(){window.dataLayer.push(arguments)}
          
          // Safe localStorage parsing for consent mode
          function safeParseLocalStorage(key, fallback = null) {
            try {
              // Check if window and localStorage are available (SSR-safe)
              if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
                return fallback;
              }
              
              var value = localStorage?.getItem(key);
              if (!value || value === 'undefined' || value === 'null') {
                return fallback;
              }
              return JSON.parse(value);
            } catch (error) {
              // eslint-disable-next-line no-console
              console.warn('[StartEngine] Failed to parse localStorage value for key "' + key + '":', error);
              return fallback;
            }
          }
          
          gtag("consent", "default", {
            ...(safeParseLocalStorage('consentMode') || {}),
            wait_for_update: 2000
          });     
        `}
      </Script>
      <Script id="ga-compatibility-script" strategy="lazyOnload">
        {`
          // Compatibility layer for legacy ga() calls to work with gtag()
          window.ga = window.ga || function() {
            var args = Array.prototype.slice.call(arguments);
            var command = args[0];
            
            // Handle common ga() commands by translating to gtag equivalents
            if (command === 'create') {
              // ga('create', 'UA-XXXXX-Y', 'auto') -> handled by GTM
              return;
            } else if (command === 'send') {
              var hitType = args[1];
              if (hitType === 'pageview') {
                // ga('send', 'pageview', '/path') -> gtag('event', 'page_view', {...})
                var page = args[2] || window.location.pathname;
                gtag('event', 'page_view', {
                  page_location: window.location.href,
                  page_path: page
                });
              } else if (hitType === 'event') {
                // ga('send', 'event', category, action, label, value) -> gtag('event', action, {...})
                var category = args[2];
                var action = args[3];
                var label = args[4];
                var value = args[5];
                var eventParams = {
                  event_category: category
                };
                if (label !== undefined) eventParams.event_label = label;
                if (value !== undefined) eventParams.value = value;
                gtag('event', action, eventParams);
              }
            } else if (command === 'set') {
              // ga('set', field, value) -> gtag('config', 'GA_MEASUREMENT_ID', {field: value})
              var field = args[1];
              var value = args[2];
              if (field && value !== undefined) {
                var configParams = {};
                configParams[field] = value;
                gtag('config', 'GA_MEASUREMENT_ID', configParams);
              }
            }
            // For any other ga() calls, just log them for debugging
            console.log('[GA Compatibility] Unhandled ga() call:', args);
          };
          
          // Also provide ga.q array for queued calls (common pattern)
          window.ga.q = window.ga.q || [];
        `}
      </Script>
      {/* Main GTM now loaded via @next/third-parties GoogleTagManager in app/layout.jsx */}
      {verificadaDefensiveScript && (
        <Script strategy="afterInteractive" id="verificada-defensive-script">
          {verificadaDefensiveScript}
        </Script>
      )}
      {gtmIssuerScript && (
        <>
          {/* Issuer GTM container + its jQuery dependency are deferred to
              `lazyOnload` (PD perf, Greenfield Robotics Jun 2026 audit).
              The issuer container fires the heavy marketing pixels (FB
              Pixel ~1.3MB, Criteo, TikTok, Bing UET, Beehiiv, Spotify,
              AdRoll, LiveIntent, Dataxu, StackAdapt, Outbrain, Taboola).
              At `afterInteractive` they all started downloading at
              ~T+2.7s and contended with the LCP hero image fetch,
              pushing LCP into the 4.0–4.4s "Poor" band. `lazyOnload`
              defers them until window `load`, reclaiming bandwidth/CPU
              for the hero image and offering API fan-out.
              The consent default (`gtag('consent','default',...)` above)
              is also `lazyOnload` and declared before this block, so
              consent state is established before the issuer container
              fires its tags. Page-view events still capture — they just
              fire after load instead of during it. */}
          <Script
            src="https://code.jquery.com/jquery-3.7.1.min.js"
            integrity="sha256-/JqT3SQfawRcv/BIHPThkBvs0OEvtFFmqPF/lYI/Cxo="
            crossOrigin="anonymous"
            strategy="lazyOnload"
            id="jquery-script"
            onLoad={() => {
              // eslint-disable-next-line no-console
              console.log('[StartEngine] jQuery loaded successfully');
              // Dispatch custom event to signal jQuery is ready
              if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('jqueryReady'));
              }
            }}
            onError={(e) => {
              // eslint-disable-next-line no-console
              console.error('[StartEngine] Failed to load jQuery:', e);
            }}
          />
          <Script strategy="lazyOnload" id="gtm-script-issuer">
            {gtmIssuerScript}
          </Script>
        </>
      )}
      <Script
        strategy="lazyOnload"
        src={process.env.NEXT_PUBLIC_FINGERPRINT_SCRIPT}
        integrity={
          process.env.NEXT_PUBLIC_FINGERPRINT_SCRIPT
            ? SCRIPT_SRI_HASHES[process.env.NEXT_PUBLIC_FINGERPRINT_SCRIPT]
            : undefined
        }
        crossOrigin={
          process.env.NEXT_PUBLIC_FINGERPRINT_SCRIPT &&
          SCRIPT_SRI_HASHES[process.env.NEXT_PUBLIC_FINGERPRINT_SCRIPT]
            ? 'anonymous'
            : undefined
        }
        onError={(error) => {
          // eslint-disable-next-line no-console
          console.warn('[ThirdPartyScripts] Fingerprint script failed to load:', error);
        }}
      />
    </>
  );
};

export default ThirdPartyScripts;
